import { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

const PAGE_PARAM = 'page';
const PAGE_SIZE_PARAM = 'pageSize';

/**
 * Hook to sync AgGrid pagination with URL search params
 * @param {Object} options
 * @param {boolean} [options.enabled=false] - Whether to sync with URL
 * @param {number} [options.defaultPageSize=20] - Default page size
 * @param {Function} [options.onGridReady] - Original onGridReady callback
 * @param {Function} [options.onPaginationChanged] - Original onPaginationChanged callback
 */
export function usePaginationSearchParams({
  enabled = false,
  defaultPageSize = 20,
  onGridReady,
  onPaginationChanged,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const isGridReadyRef = useRef(false);

  // Get initial values from URL
  const initialPage = enabled ? parseInt(searchParams.get(PAGE_PARAM) || '0', 10) : 0;
  const initialPageSize = enabled
    ? parseInt(searchParams.get(PAGE_SIZE_PARAM) || String(defaultPageSize), 10)
    : defaultPageSize;

  const lastPaginationRef = useRef({ page: initialPage, pageSize: initialPageSize });

  const handleGridReady = event => {
    if (enabled) {
      if (initialPage > 0) {
        event.api.paginationGoToPage(initialPage);
      }
      const pageSize = event.api.paginationGetPageSize();
      lastPaginationRef.current = { page: initialPage, pageSize };
      setSearchParams(
        prev => {
          const newParams = new URLSearchParams(prev);
          if (initialPage > 0) {
            newParams.set(PAGE_PARAM, String(initialPage));
          }
          newParams.set(PAGE_SIZE_PARAM, String(pageSize));
          return newParams;
        },
        { replace: true },
      );
    }
    isGridReadyRef.current = true;
    onGridReady?.(event);
  };

  const handlePaginationChanged = event => {
    if (enabled && event.api && isGridReadyRef.current) {
      const currentPage = event.api.paginationGetCurrentPage();
      const pageSize = event.api.paginationGetPageSize();

      if (
        currentPage !== lastPaginationRef.current.page ||
        pageSize !== lastPaginationRef.current.pageSize
      ) {
        lastPaginationRef.current = { page: currentPage, pageSize };
        setSearchParams(
          prev => {
            const newParams = new URLSearchParams(prev);
            if (currentPage > 0) {
              newParams.set(PAGE_PARAM, String(currentPage));
            } else {
              newParams.delete(PAGE_PARAM);
            }
            newParams.set(PAGE_SIZE_PARAM, String(pageSize));
            return newParams;
          },
          { replace: true },
        );
      }
    }
    onPaginationChanged?.(event);
  };

  return {
    initialPageSize,
    handleGridReady,
    handlePaginationChanged,
  };
}
